// React
import React from 'react';
// External
import { withRouter } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import AWS from 'aws-sdk';
import Q from 'q';
// Internal
import PageTitle from 'components/shared/atoms/PageTitle/PageTitle';
import VesselImage from 'components/shared/atoms/VesselImage/VesselImage';
import FooterBtnActions from 'components/shared/molecules/FooterBtnActions/FooterBtnActions';
import ContentCard from 'components/shared/organisms/ContentCard/ContentCard';
import NavbarPrimary from 'components/shared/organisms/NavbarPrimary/NavbarPrimary';
import EngravingImages from 'components/shared/atoms/EngravingImages/EngravingImages';
import PreResizedImage from 'components/shared/organisms/PreResizedImage/PreResizedImage';
import { clearEngraving, updateEngraving } from 'stores/actions';
import Engraving from 'models/Engraving';
import Booking from 'models/Booking';
import { getBooking } from 'services/booking';
import config from 'config/config';
import signOutCognito from 'utils/signOutCognito';
// Style
import './styles.scss';
// Images
import spinner from 'assets/icons/spinner.svg';

class EngravingExisting extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            bookingId: '',
            quotedProductId: '',
            engraving: null,
            savedImage: null,
            vesselImage: null,
            isPageLoading: false,
            staff: 0,
            nextStepPath: ''
        };
    }

    componentDidMount() {
        const params = new URLSearchParams(window.location.search);

        const paramBookingId = params.get('bookingId');
        const paramQuotedProductId = params.get('quotedProductId');

        if (!params.get('staff') || !paramBookingId || !paramQuotedProductId) {
            this.props.history.push({
                pathname: '/engraving/404',
                search: null
            });
        }

        const staff = parseInt(params.get('staff'), 10);
        if (staff) {
            if (sessionStorage.getItem(config.GUEST_TOKEN) !== null) {
                signOutCognito();
            }

            if (!localStorage.getItem(config.TOKEN)) {
                this.props.history.push(`/engraving/staff-login/${paramBookingId}/${paramQuotedProductId}`);
            }
        } else if (!sessionStorage.getItem(config.CLIENT_TOKEN)) {
            this.props.history.push(`/engraving/verify/${paramBookingId}/${paramQuotedProductId}`);
        }

        this.setState({
            isPageLoading: true,
            bookingId: paramBookingId,
            quotedProductId: paramQuotedProductId,
            staff
        }, () => {
            this.getEngraving();
        });
    }

    onEdit() {
        this.navigateTo(`/engraving/${this.state.nextStepPath}`, {
            engraving: this.props.engraving
        });
    }

    /**
     * @todo Add approve engraving request
     */
    onSubmit() {
        this.navigateTo('/engraving/thank-you');
    }

    /**
     * Sets the nexy step navigation. Only when the customise text option is available it will set the value to
     * navigate to customise text, otherwise it'll be to image options
     */
    setSteps(productId) {
        if (!productId) {
            return;
        }
        fetch('../fpc_config.json')
            .then((r) => r.json())
            .then((data) => {
                const selectedConfig = data.Config_File.find((x) => x.SugarID === productId);
                if (!selectedConfig) {
                    return;
                }
                this.setState({
                    nextStepPath: selectedConfig.Dimensions === '0' ? 'image-options' : 'customise-text'
                });
            }).catch((err) => {
                console.error(err);
            });
    }

    async getBookingDetails() {
        if (!this.state.bookingId) {
            return;
        }

        let data = null;

        await getBooking(this.state.bookingId, this.state.staff)
            .then((response) => {
                data = Booking(response.data);
            }).catch((err) => {
                console.error('err :>> ', err);
            });

        return data;
    }

    // eslint-disable-next-line class-methods-use-this
    async getS3File(s3Obj) {
        AWS.config.update({ region: config.AWS_S3.REGION });
        const s3Client = new AWS.S3({
            accessKeyId: config.AWS_S3.ACCESS_KEY,
            secretAccessKey: config.AWS_S3.SECRET_ACCESS_KEY
        });

        const params = {
            Bucket: config.AWS_S3.BUCKET,
            Key: s3Obj.Key
        };

        const deferS3Data = Q.defer();
        s3Client.getObject(params, async (err, s3Data) => {
            if (err) {
                deferS3Data.reject(err);
            } else {
                deferS3Data.resolve(s3Data);
            }
        });
        const s3File = await deferS3Data.promise;
        const s3ObjUrl = window.URL.createObjectURL(new window.Blob([s3File.Body], { type: s3File.DataType }));

        const deferS3ObjUrl = Q.defer();
        this.toDataURL(s3ObjUrl,
            (url) => {
                deferS3ObjUrl.resolve(url);
            });
        return deferS3ObjUrl.promise;
    }

    /**
     * Get engraving data and update the engraving data on redux
     */
    async getEngraving() {
        // Clears the engraving to start
        this.props.clearEngraving();

        const data = await this.getBookingDetails();

        if (data == null) {
            alert('No data to show!');
            return;
        }

        const selectedProduct = data.quotedProducts.filter((product) => product.id === this.state.quotedProductId);

        if (selectedProduct.length < 1) {
            alert('No product engraved!');
            return;
        }

        const engravingFiles = JSON.parse(selectedProduct[0].engravingFiles);

        const imageOptionsDataUrl = await this.getS3File(
            engravingFiles.params.engraving_files[0].imageOptionsUrl
        );

        const engravingOptionsDataUrl = await this.getS3File(
            engravingFiles.params.engraving_files[1].engravingOptionsUrl
        );

        const engraving = Engraving({
            ...engravingFiles.params.others,
            imageOptions: {
                ...engravingFiles.params.others.imageOptions,
                croppedImage: imageOptionsDataUrl
            },
            engravingOptionsImage: engravingOptionsDataUrl
        });
        this.setSteps(engraving.productId);
        this.props.updateEngraving(engraving);
        const vesselImage = await VesselImage(engraving.productId);
        this.setState({
            isPageLoading: false,
            engraving: engravingOptionsDataUrl,
            savedImage: imageOptionsDataUrl,
            vesselImage
        }, () => {
            this.calculateCanvasSize();
        });
    }

    async calculateCanvasSize() {
        const stateSavedImage = this.state.savedImage;
        const resultImage = await PreResizedImage(stateSavedImage, 398);
        this.setState({
            savedImage: resultImage
        });

        if (this.state.vesselImage) {
            const stateVesselImage = this.state.vesselImage;
            const vesselResultImage = await PreResizedImage(stateVesselImage, 398);
            this.setState({
                vesselImage: vesselResultImage
            });
        }
    }

    // eslint-disable-next-line class-methods-use-this
    toDataURL(url, callback) {
        const xhr = new window.XMLHttpRequest();
        xhr.onload = () => {
            const reader = new window.FileReader();
            reader.onloadend = () => {
                callback(reader.result);
            };
            reader.readAsDataURL(xhr.response);
        };
        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.send();
    }

    navigateTo(path) {
        this.props.history.push({
            pathname: path,
            search: this.props.location.search
        });
    }

    render() {
        return (
            <div className="main-container main-container-engraving">
                <NavbarPrimary hideNav />
                <div className="body-content engraving">
                    <PageTitle title="Engraving Approval" />

                    {this.state.isPageLoading ? (
                        <img
                            className="page-loading-icon"
                            alt="Page Loading Icon"
                            src={spinner}
                        />
                    ) : (
                        <ContentCard
                            className="mt-62"
                            headerTitle="Check existing artwork"
                            headerStyle={{ marginBottom: '30px' }}
                            divider={false}
                        >
                            <Container fluid className="engraving-container">
                                <EngravingImages
                                    engraving={this.state.engraving}
                                    savedImage={this.state.savedImage}
                                    vesselImage={this.state.vesselImage}
                                />
                                <Row>
                                    <Col md={12}>
                                        <p>
                                            During your booking, you submitted details for customised engraving artwork,
                                            pictured here. Please check artwork thoroughly to ensure it is error-free.
                                        </p>
                                        <p>
                                            If you are unhappy with the submitted version and wish to make changes,
                                            click ‘I want to make changes’ below to change your options.
                                        </p>
                                    </Col>
                                </Row>
                            </Container>
                        </ContentCard>
                    ) }
                </div>

                <FooterBtnActions
                    className="footer-btn-container"
                    prevBtnText="I want to make changes"
                    prevBtnOnCLick={() => this.onEdit()}
                    nextBtnText="I approve this artwork"
                    nextBtnOnCLick={() => this.onSubmit()}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        engraving: state.engraving
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        updateEngraving: (engravingObj) => dispatch(updateEngraving(engravingObj)),
        clearEngraving: (engravingObj) => dispatch(clearEngraving(engravingObj))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EngravingExisting));
